import { IComponentOptions, IPromise } from "angular";
import { IModalService } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angular";
import { Unsubscribe } from "redux";
import { IModalComponent } from "@gtmhub/core/routing";
import { CustomFieldActions } from "@gtmhub/customFields/redux/custom-field-actions";
import { ICustomFieldsState } from "@gtmhub/customFields/redux/custom-field-reducer";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { INgRedux } from "@gtmhub/state-management";
import { ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { IWorkflowSelectionField } from "@webapp/sessions/models/sessions.model";
import { krRegularFields } from "./kr-regular-fields";
import { okrRegularFields } from "./okr-regular-fields";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ApprovalWorkflowSelectionFormCtrl extends IModalComponent<{ initialUnSelectedOkrFields: string[]; initialUnSelectedKrFields: string[] }> {}
export class ApprovalWorkflowSelectionFormCtrl {
  indicators: {
    loading?: IIndicator;
  };

  isObjectiveAccordionOpened = true;
  isKeyResultAccordionOpened = false;

  regularOkrFields: IWorkflowSelectionField[];
  customOkrFields: IWorkflowSelectionField[];
  regularKrFields: IWorkflowSelectionField[];
  customKrFields: IWorkflowSelectionField[];

  cfState: ICustomFieldsState;
  reduxError: IUIError;

  private unsubscribeFromStore: Unsubscribe;

  static $inject = ["$scope", "$state", "CustomFieldActions", "$ngRedux", "UIErrorHandlingService"];
  constructor(
    private $scope: IExtendedRootScopeService,
    private $state: StateService,
    private customFieldActions: CustomFieldActions,
    private $ngRedux: INgRedux,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {}

  resolve: { initialUnSelectedOkrFields: string[]; initialUnSelectedKrFields: string[]; sessionId: string };

  $onInit(): void {
    this.indicators = {
      loading: { progress: true },
    };

    const bindStateToScope = (state) => {
      return {
        cfState: state.customFields,
      };
    };
    this.unsubscribeFromStore = this.$ngRedux.connect(bindStateToScope.bind(this.$state))(this);
    this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());
    this.$scope
      .watchUntilAndRejectOnError(
        () => this.cfState.isFetched,
        () => this.reduxError
      )
      .then(() => {
        this.customOkrFields = this.mapCustomToWorkflowSelectionFields(this.cfState.map.goal, this.resolve.initialUnSelectedOkrFields);
        this.customKrFields = this.mapCustomToWorkflowSelectionFields(this.cfState.map.metric, this.resolve.initialUnSelectedKrFields);
        this.initRegularFields();
        delete this.indicators.loading;
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
        delete this.indicators.loading;
      });
  }

  $onDestroy(): void {
    this.unsubscribeFromStore();
  }

  initRegularFields(): void {
    this.initObjectiveRegularFields();
    this.initKRRegularFields();
  }

  mapCustomToWorkflowSelectionFields(customFields: ICustomField[], unselectedFields: string[]): IWorkflowSelectionField[] {
    return customFields
      ? customFields.reduce((acc, customField) => {
          if (!customField.targetIds || customField.targetIds.length === 0 || customField.targetIds.includes(this.resolve.sessionId)) {
            acc.push({
              name: customField.name,
              label: customField.label,
              checked: this.isFieldChecked(customField.name, unselectedFields),
            });
          }
          return acc;
        }, [])
      : [];
  }

  apply() {
    const unSelectedOkrFields = this.getUnselectedOkrFields();
    const unSelectedKrFields = this.getUnselectedKrFields();
    this.close({ $value: { unSelectedOkrFields, unSelectedKrFields } });
  }

  getUnselectedOkrFields(): string[] {
    return [...this.getUnSelectedFieldsNames(this.regularOkrFields), ...this.getUnSelectedFieldsNames(this.customOkrFields)];
  }

  getUnselectedKrFields(): string[] {
    return [...this.getUnSelectedFieldsNames(this.regularKrFields), ...this.getUnSelectedFieldsNames(this.customKrFields)];
  }

  getUnSelectedFieldsNames(fields: IWorkflowSelectionField[]): string[] {
    return fields
      ? fields.reduce((acc, field) => {
          if (!field.checked) {
            acc.push(field.name);
          }
          return acc;
        }, [])
      : [];
  }

  isFieldChecked(fieldName: string, unselectedFields: string[] | null | undefined): boolean {
    return !unselectedFields?.includes(fieldName);
  }

  initObjectiveRegularFields(): void {
    this.regularOkrFields = okrRegularFields.map((regularField) => {
      return this.mapRegularToWorkflowSelectionFields(regularField, this.resolve.initialUnSelectedOkrFields);
    });
  }

  // Initial regular fields are still work in progress, we need to change name property to match BE
  initKRRegularFields(): void {
    this.regularKrFields = krRegularFields.map((regularField) => {
      return this.mapRegularToWorkflowSelectionFields(regularField, this.resolve.initialUnSelectedKrFields);
    });
  }

  mapRegularToWorkflowSelectionFields(regularField: IWorkflowSelectionField, unselectedFields: string[]): IWorkflowSelectionField {
    return {
      ...regularField,
      checked: this.isFieldChecked(regularField.name, unselectedFields),
    };
  }
}

export class ApprovalWorkflowSelectionModal {
  static $inject = ["$uibModal"];

  constructor(private $uibModal: IModalService) {}

  open(initialUnSelectedOkrFields: string[], initialUnSelectedKrFields: string[], sessionId: string): IPromise<{ unSelectedOkrFields; unSelectedKrFields }> {
    return this.$uibModal.open({
      component: "approvalWorkflowSelectionForm",
      resolve: {
        initialUnSelectedOkrFields: () => initialUnSelectedOkrFields,
        initialUnSelectedKrFields: () => initialUnSelectedKrFields,
        sessionId: () => sessionId,
      },
      windowClass: "medium session-form",
      backdropClass: "dark-backdrop",
    }).result;
  }
}

export const ApprovalWorkflowSelectionFormComponent: IComponentOptions = {
  controller: ApprovalWorkflowSelectionFormCtrl,
  template: require("./approval-workflow-selection-form.html"),

  bindings: {
    resolve: "<",
    close: "&",
    dismiss: "&",
  },
};
